import {
  COPILOT_MAIN_AGENT_LABEL,
  COPILOT_SUBAGENT_LABEL_PREFIX,
  DataMessageTypeEnum,
  type TerraUIMessage,
} from '@terra/shared-dto-chat-types';

export const TOKEN_USAGE_CONSTANTS = {
  charsPerToken: 4,
  contextWindowTokens: 200_000,
  reserveTokens: 20_000,
  triggerFraction: 0.85,
  targetFraction: 0.5,
  baseOverheadTokens: 25_000,
} as const;

const TASK_TOOL_NAME = 'task';

const SUB_AGENT_DISPLAY_NAMES = new Set([
  'recon',
  'operator',
  'object-creator',
  'background-attack',
  'application-data-analyst',
  'skill-builder',
  'code-analyst',
  'sub-agent',
]);

export type AgentRunStatus = 'running' | 'done' | 'error';
export type AgentRunTokenSource = 'exact' | 'metadata' | 'estimated';

export type AgentRun = {
  id: string;
  subAgentType: string;
  status: AgentRunStatus;
  tokens: number;
  tokenSource: AgentRunTokenSource;
};

export type TokenUsageSegments = {
  system: number;
  conversation: number;
  tools: number;
};

export type TokenUsageEstimate = {
  orchestratorTokens: number;
  orchestratorTokenSource: AgentRunTokenSource;
  effectiveWindowTokens: number;
  triggerTokens: number;
  targetTokens: number;
  usedFraction: number;
  fillFraction: number;
  triggerMarkerFraction: number;
  tokensUntilCompaction: number;
  estimatedTurnsUntilCompaction: number | null;
  isOverTrigger: boolean;
  exchangeCount: number;
  compactionCount: number;
  agentRuns: AgentRun[];
  segments: TokenUsageSegments | null;
};

type LooseToolCallData = {
  type?: unknown;
  toolName?: unknown;
  toolCallId?: unknown;
  status?: unknown;
  input?: unknown;
  output?: unknown;
  metadata?: { totalTokens?: unknown } | null;
};

type LooseBreakdown = {
  systemTokens?: unknown;
  toolsTokens?: unknown;
  conversationTokens?: unknown;
};

type LooseTokenUsageData = {
  type?: unknown;
  agent?: unknown;
  inputTokens?: unknown;
  totalTokens?: unknown;
  contextWindowTokens?: unknown;
  reserveTokens?: unknown;
  effectiveWindowTokens?: unknown;
  triggerTokens?: unknown;
  compactionCount?: unknown;
  breakdown?: LooseBreakdown | null;
};

type LooseMessagePart = {
  type?: unknown;
  text?: unknown;
  data?: (LooseToolCallData & LooseTokenUsageData) | null;
};

type ExactUsage = {
  inputTokens: number;
  contextWindowTokens: number | null;
  reserveTokens: number | null;
  effectiveWindowTokens: number | null;
  triggerTokens: number | null;
  compactionCount: number | null;
  breakdown: TokenUsageSegments | null;
};

type AgentRunAccumulator = {
  id: string;
  subAgentType: string;
  status: AgentRunStatus;
  bracketedTokens: number;
  metadataTotalTokens: number | null;
};

const estimateTokensFromChars = (charCount: number): number =>
  Math.ceil(charCount / TOKEN_USAGE_CONSTANTS.charsPerToken);

const serializeValue = (value: unknown): string => {
  if (typeof value === 'string') {
    return value;
  }

  if (value === undefined || value === null) {
    return '';
  }

  try {
    return JSON.stringify(value);
  } catch {
    return '';
  }
};

const serializePart = (part: LooseMessagePart): string => {
  if (typeof part.text === 'string') {
    return part.text;
  }

  return serializeValue(part);
};

const readSubAgentType = (input: unknown): string => {
  if (input && typeof input === 'object') {
    const record = input as Record<string, unknown>;
    const candidate = record.subagent_type ?? record.subAgentType;

    if (typeof candidate === 'string' && candidate.length > 0) {
      return candidate;
    }
  }

  return 'sub-agent';
};

const isToolCallData = (data: LooseToolCallData): boolean =>
  typeof data.toolName === 'string';

const isSubAgentSpawn = (data: LooseToolCallData): boolean => {
  if (typeof data.toolName !== 'string') {
    return false;
  }

  return (
    data.toolName === TASK_TOOL_NAME ||
    SUB_AGENT_DISPLAY_NAMES.has(data.toolName) ||
    readSubAgentType(data.input) !== 'sub-agent'
  );
};

const resolveSpawnType = (data: LooseToolCallData): string => {
  if (
    typeof data.toolName === 'string' &&
    data.toolName !== 'sub-agent' &&
    SUB_AGENT_DISPLAY_NAMES.has(data.toolName)
  ) {
    return data.toolName;
  }

  const fromInput = readSubAgentType(data.input);

  if (fromInput !== 'sub-agent') {
    return fromInput;
  }

  if (typeof data.toolName === 'string' && data.toolName !== TASK_TOOL_NAME) {
    return data.toolName;
  }

  return 'sub-agent';
};

const normalizeRunStatus = (status: unknown): AgentRunStatus => {
  if (status === 'error' || status === 'aborted') {
    return 'error';
  }

  if (status === 'completed' || status === 'result') {
    return 'done';
  }

  return 'running';
};

const readMetadataTotalTokens = (data: LooseToolCallData): number | null =>
  data.metadata && typeof data.metadata.totalTokens === 'number'
    ? data.metadata.totalTokens
    : null;

const readBreakdown = (
  breakdown: LooseBreakdown | null | undefined,
): TokenUsageSegments | null => {
  if (
    !breakdown ||
    typeof breakdown.systemTokens !== 'number' ||
    typeof breakdown.toolsTokens !== 'number' ||
    typeof breakdown.conversationTokens !== 'number'
  ) {
    return null;
  }

  return {
    conversation: breakdown.conversationTokens,
    system: breakdown.systemTokens,
    tools: breakdown.toolsTokens,
  };
};

const readOptionalNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

const readExactUsage = (
  data: LooseTokenUsageData,
): { agent: string; usage: ExactUsage } | null => {
  if (
    data.type !== DataMessageTypeEnum.TokenUsage ||
    typeof data.agent !== 'string' ||
    typeof data.inputTokens !== 'number'
  ) {
    return null;
  }

  return {
    agent: data.agent,
    usage: {
      breakdown: readBreakdown(data.breakdown),
      compactionCount: readOptionalNumber(data.compactionCount),
      contextWindowTokens: readOptionalNumber(data.contextWindowTokens),
      effectiveWindowTokens: readOptionalNumber(data.effectiveWindowTokens),
      inputTokens: data.inputTokens,
      reserveTokens: readOptionalNumber(data.reserveTokens),
      triggerTokens: readOptionalNumber(data.triggerTokens),
    },
  };
};

const scaleSegments = (
  breakdown: TokenUsageSegments,
  total: number,
): TokenUsageSegments => {
  const rawSum = breakdown.system + breakdown.conversation + breakdown.tools;

  if (rawSum <= 0) {
    return { conversation: 0, system: 0, tools: 0 };
  }

  const factor = total / rawSum;

  return {
    conversation: Math.round(breakdown.conversation * factor),
    system: Math.round(breakdown.system * factor),
    tools: Math.round(breakdown.tools * factor),
  };
};

const toAgentRun = (
  accumulator: AgentRunAccumulator,
  exactBySubAgentType: Map<string, ExactUsage>,
): AgentRun => {
  const exact = exactBySubAgentType.get(accumulator.subAgentType);

  if (exact) {
    return {
      id: accumulator.id,
      status: accumulator.status,
      subAgentType: accumulator.subAgentType,
      tokenSource: 'exact',
      tokens: exact.inputTokens,
    };
  }

  const hasMetadata = accumulator.metadataTotalTokens !== null;

  return {
    id: accumulator.id,
    status: accumulator.status,
    subAgentType: accumulator.subAgentType,
    tokenSource: hasMetadata ? 'metadata' : 'estimated',
    tokens: hasMetadata
      ? (accumulator.metadataTotalTokens as number)
      : accumulator.bracketedTokens,
  };
};

export const estimateTokenUsage = (
  messages: TerraUIMessage[],
): TokenUsageEstimate => {
  const runsById = new Map<string, AgentRunAccumulator>();
  const openRunStack: string[] = [];
  const exactByAgent = new Map<string, ExactUsage>();
  let estimatedOrchestratorTokens = 0;
  let exchangeCount = 0;

  const addBracketedTokens = (runId: string, tokens: number): void => {
    const run = runsById.get(runId);

    if (run) {
      run.bracketedTokens += tokens;
    }
  };

  const attributeTokens = (tokens: number): void => {
    const currentRunId = openRunStack[openRunStack.length - 1];

    if (currentRunId) {
      addBracketedTokens(currentRunId, tokens);
    } else {
      estimatedOrchestratorTokens += tokens;
    }
  };

  messages.forEach((message, messageIndex) => {
    if (message.role === 'user') {
      exchangeCount += 1;
    }

    (message.parts ?? []).forEach((part, partIndex) => {
      const loosePart = part as LooseMessagePart;
      const data = loosePart.data;

      if (data) {
        const exact = readExactUsage(data);

        if (exact) {
          exactByAgent.set(exact.agent, exact.usage);

          return;
        }
      }

      const tokens = estimateTokensFromChars(serializePart(loosePart).length);

      if (data && isToolCallData(data) && isSubAgentSpawn(data)) {
        const id =
          typeof data.toolCallId === 'string' && data.toolCallId.length > 0
            ? data.toolCallId
            : `${messageIndex}:${partIndex}`;
        const status = normalizeRunStatus(data.status);
        const subAgentType = resolveSpawnType(data);
        const existing = runsById.get(id);

        if (!existing) {
          runsById.set(id, {
            bracketedTokens: 0,
            id,
            metadataTotalTokens: readMetadataTotalTokens(data),
            status,
            subAgentType,
          });
        } else {
          existing.status = status;
          existing.metadataTotalTokens =
            readMetadataTotalTokens(data) ?? existing.metadataTotalTokens;
          if (subAgentType !== 'sub-agent') {
            existing.subAgentType = subAgentType;
          }
        }

        if (status === 'running') {
          if (!openRunStack.includes(id)) {
            openRunStack.push(id);
          }

          addBracketedTokens(id, tokens);
        } else {
          addBracketedTokens(id, tokens);
          const stackIndex = openRunStack.lastIndexOf(id);

          if (stackIndex !== -1) {
            openRunStack.splice(stackIndex, 1);
          }
        }

        return;
      }

      attributeTokens(tokens);
    });
  });

  const exactMain = exactByAgent.get(COPILOT_MAIN_AGENT_LABEL);
  const orchestratorTokens = exactMain
    ? exactMain.inputTokens
    : estimatedOrchestratorTokens + TOKEN_USAGE_CONSTANTS.baseOverheadTokens;
  const orchestratorTokenSource: AgentRunTokenSource = exactMain
    ? 'exact'
    : 'estimated';
  const segments =
    exactMain?.breakdown && orchestratorTokens > 0
      ? scaleSegments(exactMain.breakdown, orchestratorTokens)
      : null;

  const exactBySubAgentType = new Map<string, ExactUsage>();

  exactByAgent.forEach((usage, agent) => {
    if (agent.startsWith(COPILOT_SUBAGENT_LABEL_PREFIX)) {
      exactBySubAgentType.set(
        agent.slice(COPILOT_SUBAGENT_LABEL_PREFIX.length),
        usage,
      );
    }
  });

  const agentRuns = Array.from(runsById.values()).map((accumulator) =>
    toAgentRun(accumulator, exactBySubAgentType),
  );
  const representedSubAgentTypes = new Set(
    agentRuns.map((run) => run.subAgentType),
  );

  exactBySubAgentType.forEach((usage, subAgentType) => {
    if (representedSubAgentTypes.has(subAgentType)) {
      return;
    }

    agentRuns.push({
      id: `exact-${subAgentType}`,
      status: 'done',
      subAgentType,
      tokenSource: 'exact',
      tokens: usage.inputTokens,
    });
  });

  const contextWindowTokens =
    exactMain?.contextWindowTokens ?? TOKEN_USAGE_CONSTANTS.contextWindowTokens;
  const reserveTokens =
    exactMain?.reserveTokens ?? TOKEN_USAGE_CONSTANTS.reserveTokens;
  const effectiveWindowTokens =
    exactMain?.effectiveWindowTokens ??
    Math.max(contextWindowTokens - reserveTokens, 1);
  const triggerTokens =
    exactMain?.triggerTokens ??
    Math.floor(effectiveWindowTokens * TOKEN_USAGE_CONSTANTS.triggerFraction);
  const targetTokens = Math.floor(
    effectiveWindowTokens * TOKEN_USAGE_CONSTANTS.targetFraction,
  );
  const tokensUntilCompaction = Math.max(triggerTokens - orchestratorTokens, 0);
  const averageTokensPerExchange =
    exchangeCount > 0 ? orchestratorTokens / exchangeCount : 0;
  const estimatedTurnsUntilCompaction =
    tokensUntilCompaction === 0
      ? 0
      : averageTokensPerExchange > 0
        ? Math.floor(tokensUntilCompaction / averageTokensPerExchange)
        : null;

  return {
    agentRuns,
    compactionCount: exactMain?.compactionCount ?? 0,
    effectiveWindowTokens,
    estimatedTurnsUntilCompaction,
    exchangeCount,
    fillFraction: Math.min(orchestratorTokens / effectiveWindowTokens, 1),
    isOverTrigger: orchestratorTokens >= triggerTokens,
    orchestratorTokenSource,
    orchestratorTokens,
    segments,
    targetTokens,
    tokensUntilCompaction,
    triggerMarkerFraction: Math.min(
      triggerTokens / Math.max(effectiveWindowTokens, 1),
      1,
    ),
    triggerTokens,
    usedFraction: orchestratorTokens / effectiveWindowTokens,
  };
};

export const formatTokenCount = (count: number): string => {
  if (count >= 1_000_000) {
    return `${(count / 1_000_000).toFixed(2)}M`;
  }

  if (count >= 1_000) {
    return `${(count / 1_000).toFixed(1)}k`;
  }

  return `${count}`;
};
